import { subMonths } from "date-fns";
import { isArray, round, toPairs, uniq } from "lodash-es";

import {
  MonitoringEventStatsType,
  MonitoringEventTypeType,
  NavigationTimingMetricsType,
  NavigatorConnection,
  SectionMonitoringStatsType,
} from "../shared/types/monitoring";

import { filterMap } from "./collections";
import { rollbarAndLogError } from "./logger";

function buildFacetQueryString({ sectionId, userId }: { sectionId: string; userId?: string }) {
  const joined = filterMap(
    toPairs({ sectionId, userId }),
    ([, value]) => Boolean(value),
    ([key, value]) => `@${key}:${value}`,
  ).join(" ");

  return encodeURIComponent(joined);
}

function buildDatadogQueryUrl(query: string) {
  const now = Date.now();
  const oneMonthAgo = subMonths(now, 1).getTime();
  return `https://app.datadoghq.com/logs?&query=${query}&from_ts=${oneMonthAgo}&to_ts=${now}&stream_sort=desc&live=true`;
}

export function buildDatadogFacetedUrl({
  sectionId,
  userId,
}: {
  sectionId: string;
  userId?: string;
}) {
  const queryString = buildFacetQueryString({ sectionId, userId });
  return buildDatadogQueryUrl(queryString);
}

interface NavigatorWithConnection extends Navigator {
  connection?: NavigatorConnection;
}

// Navigation timing metrics
const getConnectionInfo = (): NavigatorConnection | null => {
  const navigatorWithConnection = navigator as NavigatorWithConnection;
  const connection = navigatorWithConnection.connection;
  if (!connection) return null;

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
    type: connection.type,
  };
};

const getDomInteractiveTime = (navigation: PerformanceNavigationTiming) =>
  navigation.domInteractive;
const getDnsLookupTime = (navigation: PerformanceNavigationTiming) =>
  navigation.domainLookupEnd - navigation.domainLookupStart;
const getDomCompleteTime = (navigation: PerformanceNavigationTiming) => navigation.domComplete;
const getDomContentLoadedTime = (navigation: PerformanceNavigationTiming) =>
  navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
const getLoadTime = (navigation: PerformanceNavigationTiming) =>
  Math.abs(navigation.loadEventEnd - navigation.loadEventStart);
const getTcpConnectionTime = (navigation: PerformanceNavigationTiming) =>
  navigation.connectEnd - navigation.connectStart;
const getNetworkTime = (navigation: PerformanceNavigationTiming) =>
  navigation.responseEnd - navigation.fetchStart;
const getRequestResponseTime = (navigation: PerformanceNavigationTiming) =>
  navigation.responseEnd - navigation.requestStart;
const getSslHandshakeTime = (navigation: PerformanceNavigationTiming) =>
  navigation.secureConnectionStart > 0
    ? navigation.connectEnd - navigation.secureConnectionStart
    : 0;
/**
 * Calculates a performance score (0-100) based on key navigation timing metrics.
 *
 * What each metric represents:
 * - Network time (responseEnd - fetchStart): Total time from when the browser started
 *   fetching the main document until it finished receiving the response. This includes
 *   DNS lookup, TCP connection, SSL handshake, and server processing time.
 * - DOM Interactive: Time until the browser has finished parsing the HTML and the DOM
 *   is ready for user interaction (scripts may still be loading/executing).
 * - Load Event End: Time until all resources (images, stylesheets, scripts) have
 *   finished loading and the page is fully rendered.
 *
 * Scoring logic (starts at 100, deducts for slow performance):
 * - Network time: >1000ms = -20, >500ms = -10 (slow server/network)
 * - DOM Interactive: >2000ms = -20, >1000ms = -10 (heavy HTML parsing/blocking scripts)
 * - Load Event End: >3000ms = -20, >2000ms = -10 (slow resource loading)
 */
const calculatePerformanceScore = (navigation: PerformanceNavigationTiming) => {
  let score = 100;

  const networkTime = getNetworkTime(navigation);
  if (networkTime > 1000) score -= 20;
  else if (networkTime > 500) score -= 10;

  const domInteractiveTime = getDomInteractiveTime(navigation);
  if (domInteractiveTime > 2000) score -= 20;
  else if (domInteractiveTime > 1000) score -= 10;

  const loadTime = getLoadTime(navigation);
  if (loadTime > 3000) score -= 20;
  else if (loadTime > 2000) score -= 10;

  return score;
};

/**
 * Assesses network quality based on navigation timing metrics.
 *
 * What each timing phase represents:
 * - DNS Time: How long it takes to resolve the domain name to an IP address.
 *   High values indicate DNS server issues or network latency to DNS servers.
 * - Connection Time: Time to establish TCP connection (including SSL handshake if HTTPS).
 *   High values indicate network congestion, slow servers, or SSL certificate issues.
 * - Network Time: Total time for the complete request/response cycle.
 *   High values indicate slow server processing, large response sizes, or network issues.
 *
 * Quality thresholds (all conditions must be met for each level):
 * - Excellent: DNS <50ms, Connection <100ms, Network <200ms
 *   (Local network, fast DNS, good server performance)
 * - Good: DNS <100ms, Connection <200ms, Network <500ms
 *   (Typical broadband, reasonable server response)
 * - Fair: DNS <200ms, Connection <500ms, Network <1000ms
 *   (Slower connections, distant servers, or server load)
 * - Poor: Any metric exceeds fair thresholds
 *   (Network issues, server problems, or very slow connections)
 */
const assessNetworkQuality = (navigation: PerformanceNavigationTiming) => {
  const networkTime = getNetworkTime(navigation);
  const dnsTime = getDnsLookupTime(navigation);
  const connectionTime = getTcpConnectionTime(navigation);

  if (networkTime < 200 && dnsTime < 50 && connectionTime < 100) {
    return "excellent";
  }

  if (networkTime < 500 && dnsTime < 100 && connectionTime < 200) {
    return "good";
  }

  if (networkTime < 1000 && dnsTime < 200 && connectionTime < 500) {
    return "fair";
  }

  return "poor";
};

export const getNavigationTimingMetrics = () => {
  try {
    const navigationEntries = performance?.getEntriesByType("navigation");
    const navigation = navigationEntries?.[0] as PerformanceNavigationTiming | undefined;
    if (navigation) {
      return {
        timestamp: new Date().toISOString(),

        // Network metrics
        dns_lookup_ms: getDnsLookupTime(navigation),
        tcp_connection_ms: getTcpConnectionTime(navigation),
        ssl_handshake_ms: getSslHandshakeTime(navigation),
        request_response_ms: getRequestResponseTime(navigation),
        total_network_ms: getNetworkTime(navigation),

        // Page load metrics
        dom_interactive_ms: getDomInteractiveTime(navigation),
        dom_content_loaded_ms: getDomContentLoadedTime(navigation),
        dom_complete_ms: getDomCompleteTime(navigation),
        total_load_ms: getLoadTime(navigation),

        // Resource metrics
        transfer_size_bytes: navigation.transferSize,
        encoded_size_bytes: navigation.encodedBodySize,
        decoded_size_bytes: navigation.decodedBodySize,

        // Navigator connection info
        connection: getConnectionInfo(),

        // Performance indicators
        performance_score: calculatePerformanceScore(navigation),
        network_quality: assessNetworkQuality(navigation),
      } as NavigationTimingMetricsType;
    }
    return null;
  } catch (error) {
    rollbarAndLogError("performance_tracking:", error);
    return null;
  }
};

export const getParticipantCountBySectionId = (
  stats: Record<string, SectionMonitoringStatsType>,
  sectionId: string,
) => {
  const enterClassStats = stats[sectionId]?.[MonitoringEventTypeType.ENTER_CLASS];
  if (!enterClassStats || !isArray(enterClassStats.userIds)) {
    return 0;
  }
  return uniq(enterClassStats.userIds).length;
};

const defaultMonitoringEventStats: MonitoringEventStatsType = {
  count: 0,
  nAffectedUsers: 0,
  mean: null,
  percentAffectedUsers: null,
  formattedMean: "-",
};

export function computeMonitoringEventStats(
  nParticipants: number,
  userIds: string[], // userIds or targetClassUserIds
): MonitoringEventStatsType {
  if (!nParticipants) {
    return defaultMonitoringEventStats;
  }

  // Compute number of events and number of affected users
  const nEvents = userIds.length;
  const affectedUsers = uniq(userIds);
  const nAffectedUsers = affectedUsers.length;

  // TODO: maybe normalize by duration of class if we have that data (like we do in Flight)
  const mean = nEvents / nParticipants;
  const percentAffectedUsers = (nAffectedUsers / nParticipants) * 100;
  return {
    count: nEvents,
    nAffectedUsers,
    mean,
    percentAffectedUsers,
    formattedMean:
      nEvents === 0 || nParticipants <= 5
        ? `${round(mean, 2)}`
        : `${round(mean, 2)} (${round(percentAffectedUsers)}% of users)`,
  };
}

export const getStatsBySectionIdAndType = (
  stats: Record<string, SectionMonitoringStatsType>,
  sectionId: string,
  type: MonitoringEventTypeType,
  useTargetClassUserIds: boolean = false,
) => {
  const eventStats = stats[sectionId]?.[type];
  const userIds = useTargetClassUserIds
    ? eventStats?.targetClassUserIds || []
    : eventStats?.userIds || [];
  if (!eventStats || !isArray(userIds)) {
    return defaultMonitoringEventStats;
  }
  const nParticipants = getParticipantCountBySectionId(stats, sectionId);
  return computeMonitoringEventStats(nParticipants, userIds);
};
